import { N, COLORS, SHAPES, pieceCells } from "@/game/shapes";

const U = 40, PAD = 14;
const SIZE = N * U + PAD * 2;
const cx = (x) => PAD + x * U + U / 2;
const cy = (y) => PAD + y * U + U / 2;

const Piece = ({ piece, opacity = 1, outline = false, onClick }) => {
  const cells = pieceCells(piece);
  const has = new Set(cells.map(([x, y]) => `${x},${y}`));
  const fill = outline ? "#23414E" : COLORS[piece.color];
  const stroke = "none";
  const def = SHAPES[piece.shape] || {};
  const isDiag = piece.diag ?? def.diag;
  const isDisc = piece.disc ?? def.disc;
  const links = [];
  cells.forEach(([ax, ay]) => cells.forEach(([bx, by]) => {
    if (ax > bx || (ax === bx && ay >= by)) return;
    const dx = Math.abs(ax - bx), dy = Math.abs(ay - by);
    if (dx > 1 || dy > 1) return;
    const diag = dx === 1 && dy === 1;
    if (diag && !isDiag && !(has.has(`${ax},${by}`) && has.has(`${bx},${ay}`))) return;
    links.push([ax, ay, bx, by]);
  }));
  const ccx = cells.reduce((a, c) => a + c[0], 0) / cells.length, ccy = cells.reduce((a, c) => a + c[1], 0) / cells.length;
  return (
    <g opacity={opacity} onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined} data-testid={onClick ? `board-piece-${piece.id}` : undefined}>
      {isDisc ? (
        <circle cx={cx(ccx)} cy={cy(ccy)} r={U * 1.45} fill={fill} stroke={stroke} strokeWidth={3} />
      ) : (
        <>
          {links.map(([ax, ay, bx, by], i) => (
            <line key={i} x1={cx(ax)} y1={cy(ay)} x2={cx(bx)} y2={cy(by)} stroke={fill} strokeWidth={U * 0.82} strokeLinecap="round" />
          ))}
          {cells.map(([x, y]) => (
            <circle key={`${x},${y}`} cx={cx(x)} cy={cy(y)} r={U * 0.41} fill={fill} stroke={stroke} strokeWidth={3} />
          ))}
        </>
      )}
      {!outline && cells.map(([x, y]) => <circle key={`h${x},${y}`} cx={cx(x)} cy={cy(y)} r={7} fill="#8FCADE" />)}
    </g>
  );
};

export const Board = ({ svgRef, pieces, ghost, ghostValid, hint, shade, onCell, onPiece, onHover, className, interactive = true }) => {
  const cellFromEvent = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const s = SIZE / r.width;
    const x = Math.floor(((e.clientX - r.left) * s - PAD) / U), y = Math.floor(((e.clientY - r.top) * s - PAD) / U);
    return x >= 0 && y >= 0 && x < N && y < N ? [x, y] : null;
  };
  return (
    <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${SIZE} ${SIZE}`} className={className} data-testid={interactive ? "game-board" : undefined}
      onClick={interactive ? (e) => { const c = cellFromEvent(e); if (c) onCell(...c); } : undefined}
      onMouseMove={interactive ? (e) => onHover(cellFromEvent(e)) : undefined}
      onMouseLeave={interactive ? () => onHover(null) : undefined}>
      <rect x={0} y={0} width={SIZE} height={SIZE} rx={18} fill="#BCE3F2" />
      <rect x={4} y={4} width={SIZE - 8} height={SIZE - 8} rx={15} fill="none" stroke="#A8D8EC" strokeWidth={2} />
      {shade && shade.map((x) => <rect key={`s${x}`} x={PAD + x * U} y={PAD} width={U} height={N * U} fill="rgba(35,65,78,0.10)" />)}
      {Array.from({ length: N * N }, (_, i) => (
        <circle key={i} cx={cx(i % N)} cy={cy(Math.floor(i / N))} r={8} fill="#8FCADE" />
      ))}
      {hint && hint.map((p, i) => <Piece key={`hint${i}`} piece={p} outline opacity={0.16} />)}
      {pieces.map((p) => <Piece key={p.id} piece={p} onClick={interactive && onPiece ? (e) => onPiece(p.id, e) : undefined} />)}
      {ghost && <Piece piece={ghost} opacity={ghostValid ? 0.6 : 0.25} />}
    </svg>
  );
};

export const ShapeThumb = ({ shape, rot = 0, color = "rosa" }) => {
  const cells = pieceCells({ shape, rot, x: 0, y: 0 });
  const w = Math.max(...cells.map((c) => c[0])) + 1, h = Math.max(...cells.map((c) => c[1])) + 1;
  return (
    <svg viewBox={`${-PAD} ${-PAD} ${w * U + PAD * 2} ${h * U + PAD * 2}`} style={{ width: w * 15 + 10, height: h * 15 + 10 }}>
      <Piece piece={{ shape, rot, x: 0, y: 0, color }} />
    </svg>
  );
};
